import './style.css';
import { daysBetween, localDateOf, type Change, type MeResponse, type MemberDto, type MemoryDto, type SnapshotResponse } from '@star-sea/shared';
import { thumbHashToDataURL } from 'thumbhash';
import { Api, ApiError } from './api.ts';
import { fakeSnapshot } from './fake.ts';
import { Galaxy } from './galaxy.ts';
import { playIntro } from './intro.ts';
import { clearOffline, loadSnapshot, offlineStats, saveSnapshot } from './offline.ts';
import { createSound } from './sound.ts';
import type { CardImage } from './carousel.ts';
import { errorScreen, inviteDialog, loadingScreen, mountCaption, mountHud, mountIntro, mountPanel, mountShell, pairScreen, setupScreen, swatchCanvas, toast, type PanelContent } from './ui.ts';
import { deviceLabel, isHeic, uploadForm } from './upload.ts';

const api = new Api();
const appRoot = document.getElementById('app')!;
const shell = mountShell(appRoot);
const sound = createSound();
const params = new URLSearchParams(location.search);
const fakeCount = Math.min(200_000, Math.max(0, Math.floor(Number(params.get('fake') ?? 0)) || 0));
const showStats = import.meta.env.DEV || fakeCount > 0 || params.has('stats');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
// 手机和小屏：照片切换的形变粒子少一些（同原型）
const lowEnd = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 560;
// ?intro=0 跳过入场动画（压测、调试时用）
const introOn = params.get('intro') !== '0';
// 画质档位（quality.ts）：按设备记住自动调到的那档；?quality=N 固定在第 N 档（调试用，不记住）
const QUALITY_KEY = 'star-sea/quality';
const forcedQuality = params.has('quality') ? Number(params.get('quality')) || 0 : null;

function readQuality(): number {
  try {
    return Number(localStorage.getItem(QUALITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function saveQuality(level: number): void {
  try {
    localStorage.setItem(QUALITY_KEY, String(level));
  } catch {
    // 浏览器不让存：下次从头调
  }
}

const WEEKDAY = '日一二三四五六';
/** 星轨开关按设备记住（同原型）；默认开 */
const TRAIL_KEY = 'star-sea/trail';

function readTrail(): boolean {
  try {
    return localStorage.getItem(TRAIL_KEY) !== '0';
  } catch {
    return true;
  }
}

function saveTrail(on: boolean): void {
  try {
    localStorage.setItem(TRAIL_KEY, on ? '1' : '0');
  } catch {
    // 浏览器不让存：这次打开时有效
  }
}

function coordinates(lat: number, lng: number): string {
  return `${lat >= 0 ? '北纬' : '南纬'} ${Math.abs(lat).toFixed(3)}° · ${lng >= 0 ? '东经' : '西经'} ${Math.abs(lng).toFixed(3)}°`;
}

function showScreen(node: HTMLElement | null): void {
  shell.overlay.replaceChildren(...(node ? [node] : []));
}

/** 邀请链接形如 …/#invite=CODE；码放在 # 后面，不会发到服务器、也不会进访问日志 */
function takeInviteFromHash(): string {
  const m = /^#invite=([0-9A-Za-z-]+)$/.exec(location.hash);
  return m ? m[1]! : '';
}

function clearHash(): void {
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
}

/** 这台设备不能再用了（令牌失效、被移除）：令牌和离线缓存一起清掉 */
async function forget(): Promise<void> {
  api.setToken(null);
  await clearOffline();
}

/** 连不上服务时，用上次同步的快照打开星海（offline.ts）；没有缓存返回 false */
async function startOffline(): Promise<boolean> {
  if (!api.token) return false;
  const cached = await loadSnapshot();
  if (!cached) return false;
  clearHash();
  start(cached.snapshot, cached.me, { offlineSince: cached.savedAt });
  return true;
}

async function boot(): Promise<void> {
  if (fakeCount > 0) return start(fakeSnapshot(fakeCount), null);
  showScreen(loadingScreen());
  let setup: boolean;
  try {
    setup = (await api.health()).setup;
  } catch {
    // 有上次同步的星海就先显示它，连上以后自动跟上
    if (await startOffline()) return;
    // 设备明确没联网时直接说；联着网也可能上不了外网，所以另一种情况不下结论，原因都列上
    showScreen(
      navigator.onLine === false
        ? errorScreen('这台设备没有联网', '连上 Wi-Fi 或打开移动数据，再点「重试」。', () => void boot())
        : errorScreen('连不上星海服务', '星海服务没有回应：可能是网络不太好，也可能是服务正在更新、或者暂时停了。等一会儿再重试；一直连不上的话，请维护星海的那个人看看服务开着没有。', () => void boot()),
    );
    return;
  }
  if (!setup) {
    // 服务那边还没有星海（比如重新装过）：旧的令牌和离线缓存都不再有用
    await forget();
    showScreen(
      setupScreen(async (v) => {
        const s = await api.setup({ ...v, deviceLabel: deviceLabel() });
        api.setToken(s.token);
        await enter();
      }),
    );
    return;
  }
  if (!api.token) return showPair();
  await enter();
}

function showPair(): void {
  const invite = takeInviteFromHash();
  showScreen(
    pairScreen(invite, async (v) => {
      const s = await api.pair({ code: v.code, displayName: v.displayName || undefined, deviceLabel: deviceLabel() });
      api.setToken(s.token);
      clearHash();
      await enter();
    }),
  );
}

async function enter(): Promise<void> {
  try {
    const [me, snap] = await Promise.all([api.me(), api.snapshot()]);
    clearHash();
    void saveSnapshot(me, snap);
    start(snap, me);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await forget();
      return showPair();
    }
    // 健康检查过了、取快照时又断了：同样先显示离线缓存
    if (e instanceof ApiError && (e.status === 0 || e.status >= 500) && (await startOffline())) return;
    showScreen(errorScreen('打开星海时出错了', e instanceof Error ? e.message : String(e), () => void boot()));
  }
}

/** offlineSince：连不上服务，用的是这个时间存下的离线缓存 */
function start(snap: SnapshotResponse, me: MeResponse | null, opts: { offlineSince?: string } = {}): void {
  showScreen(null);
  const fake = me === null;
  const members = new Map<string, MemberDto>(snap.members.map((m) => [m.id, m]));
  const memories = new Map<string, MemoryDto>(snap.memories.map((m) => [m.id, m]));
  const since = snap.space.since;
  const galaxy = new Galaxy(shell.stage, shell.labels, {
    reducedMotion,
    thumbs: { fake, token: api.token },
    quality: forcedQuality !== null ? { start: forcedQuality, fixed: true } : { start: readQuality(), fixed: false, onChange: saveQuality },
  });
  // 有入场动画时，星一开始就在爱心上，不再单独做「依次点亮」
  galaxy.load(snap, !introOn);
  let introPlaying = introOn;
  if (introOn) {
    appRoot.classList.add('intro-on');
    const view = mountIntro(shell.overlay, { title: snap.space.name, sub: '我们的每一段回忆，都是一颗星' });
    void playIntro(galaxy, view, sound, { reduced: reducedMotion, onBang: () => appRoot.classList.remove('intro-on') }).then(() => (introPlaying = false));
  }
  // 浏览器要先有一次点击才能出声：第一次点击或按键时解锁并开始放背景音乐。
  // 入场期间由「轻触心跳」解锁，音乐从爆炸开始淡入
  const kick = () => {
    sound.unlock();
    if (introPlaying) return;
    sound.start(2.5);
    removeEventListener('pointerdown', kick, true);
    removeEventListener('keydown', kick, true);
  };
  addEventListener('pointerdown', kick, true);
  addEventListener('keydown', kick, true);

  const hud = mountHud(
    shell.overlay,
    {
      onHome: () => {
        exitRing(false);
        galaxy.select(-1, false);
        galaxy.frameAll(true);
      },
      onFiles: (files) => {
        if (offline) toast(shell.toasts, '现在连不上星海服务，连上以后再添照片');
        else void upload(files);
      },
      onInvite: () => inviteDialog({ canInviteMember: members.size < 2, create: (kind) => api.invite(kind) }),
      onTrail: () => {
        trailOn = !trailOn;
        saveTrail(trailOn);
        galaxy.setTrail(trailOn);
        hud.setTrail(trailOn);
        toast(shell.toasts, trailOn ? '星轨已显示：按时间连起所有回忆' : '星轨已隐藏');
      },
      music: sound,
      cache: { stats: offlineStats, clear: clearOffline },
    },
    { fake, stats: showStats },
  );
  /** 打开时连不上服务、显示的是离线缓存；实时连接接上后变回 false */
  let offline = opts.offlineSince !== undefined;
  let offlineNote: ReturnType<typeof toast> | null = null;
  if (opts.offlineSince) {
    const d = new Date(opts.offlineSince);
    const at = `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    hud.setConnection('offline');
    offlineNote = toast(shell.toasts, `现在连不上星海服务，先显示 ${at} 同步的星海；连上以后会自动跟上。`, { sticky: true });
  }
  let trailOn = readTrail();
  galaxy.setTrail(trailOn);
  hud.setTrail(trailOn);
  hud.setTitle(snap.space.name);
  const refreshCounts = () => {
    const days = daysBetween(since, localDateOf(new Date())) + 1;
    hud.setSub(`${fake ? '压测模式 · 没有照片 · ' : ''}在一起的第 ${days.toLocaleString('zh-CN')} 天 · 已点亮 ${galaxy.photos.length.toLocaleString('zh-CN')} 颗星`);
  };
  refreshCounts();
  setInterval(() => {
    const s = galaxy.stats;
    hud.setStats(`${s.fps.toFixed(0)} fps · ${s.frameMs.toFixed(1)} ms · ${s.stars.toLocaleString('zh-CN')} 颗星 · ${s.calls} 次绘制 · 画质 ${galaxy.qualityLevel}`);
  }, 500);

  // ── 照片面板 ──
  let loading: AbortController | null = null;
  const step = (delta: number) => {
    const i = galaxy.selectedIndex;
    const day = galaxy.sameDay(i);
    const k = day.indexOf(i);
    if (k >= 0 && day.length > 1) galaxy.select(day[(k + delta + day.length) % day.length]!);
  };
  /** 一颗星在面板和轮转字幕里显示的文字 */
  const describe = (i: number): PanelContent => {
    const p = galaxy.photos[i]!;
    const memory = memories.get(p.memoryId);
    const day = galaxy.sameDay(i);
    const d = new Date(`${p.date}T12:00:00`);
    const time = p.takenAt && /T(\d{2}:\d{2})/.exec(p.takenAt)?.[1];
    const nth = daysBetween(since, p.date) + 1;
    return {
      date: `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${WEEKDAY.charAt(d.getDay())}${time ? ` · ${time}` : ''}`,
      title: memory?.title ?? (nth > 0 ? `在一起的第 ${nth} 天` : '认识之前'),
      place: memory?.place?.name ?? (p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : ''),
      story: memory?.story ?? '',
      by: `由 ${members.get(p.createdBy)?.displayName ?? 'Ta'} 点亮${memory?.title && nth > 0 ? ` · 在一起的第 ${nth} 天` : ''}`,
      count: `${day.indexOf(i) + 1} / ${day.length}`,
      aspect: p.width && p.height ? p.width / p.height : 4 / 3,
      accent: p.color,
      placeholder: p.thumbhash ? thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0))) : undefined,
      swatch: fake ? `radial-gradient(circle at 30% 30%, ${p.color}, #0b0d1a)` : undefined,
      note: fake ? '压测数据没有照片' : p.thumbs ? undefined : 'HEIC 原图已经存好，服务端暂时解不开，缩略图以后补上',
    };
  };

  // ── 轮转：这一天的照片排成光环 ──
  const caption = mountCaption(shell.overlay, { onBack: () => exitRing(true) });
  /** 从光环回到面板时不放星尘：照片刚才就在眼前 */
  let quietOpen = false;
  const cardPlaceholder = async (i: number): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake) return swatchCanvas(p.color);
    if (!p.thumbhash) return null;
    const img = new Image();
    img.src = thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0)));
    await img.decode();
    return img;
  };
  const cardImage = async (i: number, signal: AbortSignal): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake || !p.thumbs) return null;
    const url = URL.createObjectURL(await api.photoBlob(p.id, 't512', signal));
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
    return img;
  };
  const enterRing = () => {
    const i = galaxy.selectedIndex;
    if (i < 0 || galaxy.inCarousel) return;
    const day = galaxy.sameDay(i);
    if (day.length < 2) return;
    panel.hide({ quiet: true });
    appRoot.classList.add('ring-on');
    galaxy.enterCarousel(day, day.indexOf(i), {
      placeholder: cardPlaceholder,
      load: cardImage,
      onFront: (j, k, n) => {
        const c = describe(j);
        caption.show({ date: c.date, title: c.title, place: c.place, count: `${k + 1} / ${n}` });
      },
      onOpen: () => exitRing(true),
    });
  };
  /** 收起光环；open 时回到正面那张的照片面板 */
  const exitRing = (open: boolean) => {
    if (!galaxy.inCarousel) return;
    const i = galaxy.leaveCarousel();
    caption.hide();
    appRoot.classList.remove('ring-on');
    if (open && i >= 0) {
      quietOpen = true;
      galaxy.select(i);
    }
  };
  /** 飞到某颗星（提示里的「去看看」）；在光环里时先收起 */
  const goTo = (i: number) => {
    exitRing(false);
    galaxy.select(i);
  };

  const panel = mountPanel(
    shell.overlay,
    { onClose: () => galaxy.select(-1, false), onPrev: () => step(-1), onNext: () => step(1), onRing: enterRing },
    // 粒子化的格点：桌面 128×128、手机 96×96（同原型）
    { morphPoints: lowEnd ? 24576 : 65536, dustGrid: lowEnd ? 96 : 128, reduced: reducedMotion, fx: shell.fx },
  );
  const narrow = matchMedia('(max-width: 640px)');
  /** 面板里正显示的那颗星 */
  let shown = -1;
  galaxy.onSelect = (i) => {
    loading?.abort();
    loading = null;
    galaxy.setViewShift(i >= 0 && narrow.matches ? 0.24 : 0);
    const before = shown;
    shown = i;
    const quiet = quietOpen;
    quietOpen = false;
    if (i < 0) return panel.hide();
    const p = galaxy.photos[i]!;
    // 面板开着时换到另一张：等清晰图到了用流体形变换过去
    const sw = panel.open && before >= 0 && before !== i && !fake && p.thumbs ? { phase: (before * 1.71 + i * 0.93) % 10 } : undefined;
    // 新打开时星尘从星里飞进画框；从光环回来时照片刚才就在眼前，直接显示
    panel.show(describe(i), { sw, from: sw || quiet ? undefined : () => galaxy.screenPosition(i) });
    if (!fake && p.thumbs) {
      const ctrl = (loading = new AbortController());
      api
        .photoBlob(p.id, 't512', ctrl.signal)
        .then((b) => {
          if (galaxy.selectedIndex === i) panel.setImage(b);
        })
        .catch(() => undefined);
    }
  };

  addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (galaxy.inCarousel) {
      // 回车只在没有按钮获得焦点时算「打开正面那张」，免得和按钮自己的回车撞上（同原型）
      if (e.key === 'Escape' || (e.key === 'Enter' && (e.target === document.body || e.target === galaxy.renderer.domElement))) exitRing(true);
      else if (e.key === 'ArrowLeft') galaxy.carouselStep(-1);
      else if (e.key === 'ArrowRight') galaxy.carouselStep(1);
      else if (e.key === 'Home') {
        exitRing(false);
        galaxy.frameAll(true);
      }
      return;
    }
    if (e.key === 'Escape' && panel.open) galaxy.select(-1, false);
    // 没开着照片：Esc 一层一层往外退，一天 → 一年 → 整片星海
    else if (e.key === 'Escape') galaxy.stepBack();
    else if (e.key === 'Home') galaxy.frameAll(true);
    else if (panel.open && e.key === 'ArrowLeft') step(-1);
    else if (panel.open && e.key === 'ArrowRight') step(1);
  });

  if (import.meta.env.DEV) Object.assign(window, { __starSea: { galaxy, api, sound } });
  if (fake || !me) return;

  // ── 实时点亮 ──
  let lit: { count: number; last: number; by: string; note: ReturnType<typeof toast> | null; timer?: ReturnType<typeof setTimeout> } | null = null;
  const partnerLit = (by: string, index: number) => {
    if (!lit || lit.by !== by) lit = { count: 0, last: index, by, note: null };
    lit.count++;
    lit.last = index;
    const name = members.get(by)?.displayName ?? 'Ta';
    const text = `${name} 刚点亮了 ${lit.count} 颗星`;
    const target = lit.last;
    if (lit.note) lit.note.done(text, { label: '去看看', onClick: () => goTo(target) }, 8000);
    else lit.note = toast(shell.toasts, text, { action: { label: '去看看', onClick: () => goTo(target) }, ms: 8000 });
    clearTimeout(lit.timer);
    lit.timer = setTimeout(() => (lit = null), 8000);
  };

  // 有变化时过几秒存一份新的快照，下次连不上服务时显示的就是最新的
  let seq = snap.seq;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  const remember = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void saveSnapshot(me, { seq, space: snap.space, members: [...members.values()], memories: [...memories.values()], photos: galaxy.photos }), 3000);
  };

  const onChange = (c: Change) => {
    seq = Math.max(seq, c.seq);
    remember();
    if (c.kind === 'photo.added') {
      memories.set(c.memory.id, c.memory);
      galaxy.setMemory(c.memory);
      const known = galaxy.indexOf(c.photo.id) >= 0;
      const i = galaxy.add(c.photo);
      if (!known) {
        refreshCounts();
        if (c.by && c.by !== me.member.id) partnerLit(c.by, i);
      }
    } else if (c.kind === 'member.joined') {
      members.set(c.member.id, c.member);
      galaxy.setMember(c.member);
      if (c.member.id !== me.member.id) toast(shell.toasts, `${c.member.displayName} 加入了星海`);
    }
  };
  api.subscribe(snap.seq, onChange, (state) => {
    if (state === 'unauthorized') {
      void forget().then(() => location.reload());
      return;
    }
    if (state === 'retrying' && offline) return;
    hud.setConnection(state);
    if (state !== 'open') return;
    // 接上了：之前断网没取到的缩略图再要一次；从离线缓存打开的，缺的变更由实时推送补上
    galaxy.pool.retryFailed();
    if (offline) {
      offline = false;
      offlineNote?.done('已经重新连上星海服务，星海会自动跟上最新的', { label: '刷新', onClick: () => location.reload() }, 10000);
    }
  });

  // ── 添照片 ──
  let busy = Promise.resolve();
  const upload = (files: File[]) =>
    (busy = busy.then(async () => {
      const note = toast(shell.toasts, `正在点亮 1 / ${files.length}…`, { sticky: true });
      let added = 0, dup = 0, failed = 0, heicPending = 0, lastIndex = -1, lastError = '';
      for (const [k, file] of files.entries()) {
        note.update(`正在点亮 ${k + 1} / ${files.length}…`);
        try {
          const res = await api.upload(await uploadForm(file), (f) => note.progress((k + f) / files.length));
          memories.set(res.memory.id, res.memory);
          galaxy.setMemory(res.memory);
          lastIndex = galaxy.add(res.photo);
          if (res.duplicate) dup++;
          else added++;
          if (!res.photo.thumbs && isHeic(file)) heicPending++;
        } catch (e) {
          failed++;
          lastError = e instanceof Error ? e.message : String(e);
        }
        note.progress((k + 1) / files.length);
      }
      refreshCounts();
      const parts = [added ? `点亮了 ${added} 颗星` : '', dup ? `${dup} 张已经在星海里了` : '', failed ? `${failed} 张没传上去（${lastError}）` : '', heicPending ? `${heicPending} 张 HEIC 暂时没有缩略图` : ''].filter(Boolean);
      const target = lastIndex;
      note.done(parts.join('，') || '没有照片', target >= 0 ? { label: '去看看', onClick: () => goTo(target) } : undefined, failed ? 12000 : 7000);
    }));
}

void boot();

// PWA：只在生产构建里注册（开发时每次改代码都要更新缓存，碍事）；注册失败不影响使用
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}
